import { TabsContent } from "@/components"
import { ComponentType, useCallback, useEffect, useMemo, useState } from "react"
import { useFetchReviewerPrinciples } from "@/hooks/queries/useFetchReviewerPrinciples"
import { useUpdateReviewerPrinciples } from "@/hooks/mutations/useUpdateReviewerPrinciples"
import { PrincipleItem } from "@/pages/settings/reviewer/customPrinciples/PrincipleItem"
import { CUSTOM_REVIEWER_TYPES } from "@/pages/settings/reviewer/customPrinciples/CustomReviewerTypes"
import { Frame } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useFetchUser } from "@/hooks/queries/useFetchUser"
import { Principle } from "@/models/Principle"
import { toast } from "@/hooks/useToast"

export const CustomPrinciples = () => {
  const [principles, setPrinciples] = useState<{ recommended: Principle[]; activated: Principle[] }>()
  const { data: reviewerPrinciplesFromBE, isLoading } = useFetchReviewerPrinciples()
  const { mutate } = useUpdateReviewerPrinciples()
  const { data: user } = useFetchUser()
  const { t } = useTranslation("Settings")
  const { t: tReviewer } = useTranslation("Reviewer")

  const filterPrinciples = (principles: Principle[], enabled: boolean) =>
    principles.filter((principle) => principle.enabled === enabled)

  useEffect(() => {
    if (!isLoading && reviewerPrinciplesFromBE) {
      setPrinciples({
        recommended: filterPrinciples(reviewerPrinciplesFromBE, false),
        activated: filterPrinciples(reviewerPrinciplesFromBE, true)
      })
    }
  }, [isLoading, reviewerPrinciplesFromBE])

  const isAdmin = useMemo(() => {
    return user && (user.is_owner || user.permissions.some((p) => p.role === "admin"))
  }, [user])

  const handleActivate = useCallback(
    async (principleId: string, isEnabled: boolean) => {
      try {
        await mutate({ principleId, enabled: isEnabled })
      } catch (err) {
        toast({
          variant: "destructive",
          description: `Error updating principle, please try again later. Error: ${err}`
        })
      }
    },
    [mutate]
  )

  const renderPrinciples = (principles: Principle[], isActivated = false) => {
    return principles.map(({ id, title, label, enabled }) => {
      const reviewerType = CUSTOM_REVIEWER_TYPES.find((reviewerType) => reviewerType.type === label)
      const IconComponent = reviewerType?.icon as ComponentType<{ size?: number }>

      return (
        <PrincipleItem
          key={id}
          id={id}
          title={title}
          label={label}
          enabled={enabled}
          isActivated={isActivated}
          isDisabled={!isAdmin}
          handleActivate={(principleId, isEnabled) => handleActivate(principleId, isEnabled)}
          icon={IconComponent ? <IconComponent size={16} /> : <Frame size={16} />}
        />
      )
    })
  }

  return (
    <TabsContent value="custom" className="w-full flex-col text-xs">
      <p className="mt-4 text-xs font-medium">{t("CustomPrinciplesExplained")}</p>

      {principles?.recommended && principles.recommended.length > 0 && (
        <section>
          <h2 className="my-4 text-xs">{t("Recommended")}</h2>
          {principles && <div className="grid grid-cols-2 gap-4">{renderPrinciples(principles.recommended)}</div>}
        </section>
      )}
      {principles?.activated && principles.activated.length > 0 && (
        <section>
          <h2 className="my-4 text-xs">{tReviewer("Activated")}</h2>
          <div className="grid grid-cols-2 gap-4">{renderPrinciples(principles.activated, true)}</div>
        </section>
      )}
    </TabsContent>
  )
}
